import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_CONFIG, AUTH_CONFIG } from './config';

export class BaseApi {
    protected axios: AxiosInstance;

    constructor() {
        this.axios = axios.create(API_CONFIG);

        // Request interceptor
        this.axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
                if (token && config.headers) {
                    config.headers.Authorization = `${AUTH_CONFIG.tokenType} ${token}`;
                }
                return config;
            },
            (error) => {
                console.error('Request error:', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor
        this.axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                if (error.response) {
                    if (error.response.status === 401) {
                        localStorage.removeItem(AUTH_CONFIG.tokenKey);
                        window.location.href = AUTH_CONFIG.loginRedirect;
                    }
                    // Handle redirect errors
                    if (error.response.status === 308) {
                        const originalRequest = error.config;
                        if (!originalRequest.url.endsWith('/')) {
                            originalRequest.url += '/';
                            return this.axios(originalRequest);
                        }
                    }
                    return Promise.reject(error.response.data);
                } else if (error.request) {
                    return Promise.reject({
                        error: {
                            message: 'No response from server',
                            details: error.request
                        }
                    });
                } else {
                    return Promise.reject({
                        error: {
                            message: 'Request configuration error',
                            details: error.message
                        }
                    });
                }
            }
        );
    }

    // Token management
    public setToken(token: string | null): void {
        if (token) {
            this.axios.defaults.headers.common['Authorization'] = `${AUTH_CONFIG.tokenType} ${token}`;
        } else {
            delete this.axios.defaults.headers.common['Authorization'];
        }
    }

    protected async makeGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        try {
            // Ensure URL ends with trailing slash
            const normalizedUrl = url.endsWith('/') ? url : `${url}/`;
            const response = await this.axios.get<T>(normalizedUrl, config);
            console.log('Axios response:', response);
            return response.data;
        } catch (error) {
            console.error(`GET request to ${url} failed:`, error);
            throw error;
        }
    }

    protected async makePost<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        try {
            const normalizedUrl = url.endsWith('/') ? url : `${url}/`;
            const response = await this.axios.post<T>(normalizedUrl, data, config);
            return response.data;
        } catch (error) {
            console.error(`POST request to ${url} failed:`, error);
            throw error;
        }
    }

    protected async makePut<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        try {
            const normalizedUrl = url.endsWith('/') ? url : `${url}/`;
            const response = await this.axios.put<T>(normalizedUrl, data, config);
            return response.data;
        } catch (error) {
            console.error(`PUT request to ${url} failed:`, error);
            throw error;
        }
    }

    protected async makePatch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        try {
            const normalizedUrl = url.endsWith('/') ? url : `${url}/`;
            const response = await this.axios.patch<T>(normalizedUrl, data, config);
            return response.data;
        } catch (error) {
            console.error(`PATCH request to ${url} failed:`, error);
            throw error;
        }
    }

    protected async makeDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        try {
            const normalizedUrl = url.endsWith('/') ? url : `${url}/`;
            const response = await this.axios.delete<T>(normalizedUrl, config);
            return response.data;
        } catch (error) {
            console.error(`DELETE request to ${url} failed:`, error);
            throw error;
        }
    }
}


